import { makeGetApiCall, makePostApiCall } from "../shared/makeApiCall";

export const QUICKBOOKS_INVOICE_STATUSES = [
  "queued",
  "waiting_dependency",
  "preparing",
  "submitting",
  "retry_wait",
  "reconciling",
  "succeeded",
  "failed",
  "needs_review",
  "blocked",
];

const ACTIVE_STATUSES = [
  "queued",
  "waiting_dependency",
  "preparing",
  "submitting",
  "retry_wait",
  "reconciling",
];

export function isActiveInvoiceStatus(status) {
  return ACTIVE_STATUSES.includes(status);
}

export function isInvoiceCreationBlocked(state) {
  return Boolean(
    state?.loading ||
    state?.polling ||
    state?.retrying ||
    state?.replacing ||
    state?.error ||
    state?.status,
  );
}

function asError(callResult, fallback) {
  return {
    message: callResult?.data?.message || fallback,
    status: callResult?.errorStatus,
    isCanceled: false,
  };
}

function validateStatus(value, shipmentId) {
  if (
    !value ||
    typeof value.request_id !== "string" ||
    typeof value.shipment_id !== "number" ||
    !QUICKBOOKS_INVOICE_STATUSES.includes(value.status) ||
    typeof value.can_retry !== "boolean" ||
    typeof value.can_replace !== "boolean" ||
    (shipmentId !== undefined && value.shipment_id !== shipmentId) ||
    (value.status === "succeeded" &&
      (value.quickbooks_invoice_id === undefined ||
        value.quickbooks_invoice_id === null ||
        value.quickbooks_invoice_id === ""))
  ) {
    const error = new Error("QuickBooks returned an invalid invoice status");
    error.code = "invalid_response";
    throw error;
  }
  return value;
}

const quickBooksInvoicesApi = {
  async getCapabilities() {
    const callResult = await makeGetApiCall(
      "quickbooks/invoice-requests/capabilities",
    );
    if (!callResult.success || typeof callResult.data?.enabled !== "boolean") {
      throw asError(callResult, "QuickBooks returned invalid capabilities");
    }
    return callResult.data;
  },

  async getLatestForShipment(shipmentId) {
    const callResult = await makeGetApiCall(
      `quickbooks/shipments/${encodeURIComponent(shipmentId)}/invoice-request`,
    );
    if (!callResult.success) {
      throw asError(callResult, "Unable to load the QuickBooks invoice status");
    }
    if (
      callResult.data === null ||
      (typeof callResult.data === "object" &&
        !Array.isArray(callResult.data) &&
        Object.keys(callResult.data).length === 0)
    ) {
      return null;
    }
    return validateStatus(callResult.data, Number(shipmentId));
  },

  async getStatus(requestId) {
    const callResult = await makeGetApiCall(
      `quickbooks/invoice-requests/${encodeURIComponent(requestId)}`,
    );
    if (!callResult.success) {
      throw asError(callResult, "Unable to load the QuickBooks invoice status");
    }
    return validateStatus(callResult.data);
  },

  async retry(requestId) {
    const callResult = await makePostApiCall(
      `quickbooks/invoice-requests/${encodeURIComponent(requestId)}/retry`,
      {},
    );
    if (!callResult.success) {
      throw asError(callResult, "Invoice could not be retried");
    }
    return validateStatus(callResult.data);
  },

  async replace(requestId) {
    const callResult = await makePostApiCall(
      `quickbooks/invoice-requests/${encodeURIComponent(requestId)}/replace`,
      {},
    );
    if (!callResult.success) {
      throw asError(callResult, "A corrected invoice could not be created");
    }
    return validateStatus(callResult.data);
  },
};

export default quickBooksInvoicesApi;
